import { useState } from 'react';
import type { Shot } from '@studio/shared';

import { api, assetUrl, sentUrl, usd, type SentStep, type SentToGemini as Sent } from '../lib/api';
import { useDateFormat } from '../lib/dates';
import { useErrorText } from '../lib/errors';
import { useSession } from '../lib/session';
import { useDialog } from './Dialog';
import { useToast } from './Toaster';
import type { Translate } from '../lib/i18n';

/**
 * For the studio's staff: what Gemini was sent for the version on show —
 * every picture in the order it went, with its caption, the words, the model
 * and the cost — and what it answered. The way to see why a photograph came
 * out as it did, instead of guessing.
 */
export function SentToGemini({ shotId, assetId, t }: { shotId: string; assetId?: string; t: Translate }) {
  const { user, authDisabled } = useSession();
  const describe = useErrorText();
  const date = useDateFormat();
  const [sent, setSent] = useState<Sent | undefined>();
  const [error, setError] = useState('');
  const [loadedFor, setLoadedFor] = useState<string | undefined>();

  if (!authDisabled && !user?.staff) return null;

  const load = () => {
    const key = `${shotId}:${assetId ?? ''}`;
    if (loadedFor === key) return;
    setLoadedFor(key);
    setSent(undefined);
    setError('');
    api
      .sentToGemini(shotId, assetId)
      .then(setSent)
      .catch((err: unknown) => setError(describe(err, 'roll.notLoaded')));
  };

  return (
    <details
      className="lb__fold sent"
      onToggle={(event) => {
        if ((event.currentTarget as HTMLDetailsElement).open) load();
      }}
    >
      <summary>
        {t('loupe.sent')}
        <span className="faint">{t('loupe.sentStaff')}</span>
      </summary>
      <div className="lb__foldBody">
        {error && <div className="banner banner--error">{error}</div>}
        {!sent && !error && <p className="faint">{t('loupe.sentLoading')}</p>}
        {sent && !sent.steps.length && !sent.refused.length && (
          <p className="faint">{t('loupe.sentEmpty')}</p>
        )}
        {sent?.refused.map((attempt) => (
          <div key={attempt.at} className="sent__step sent__step--refused">
            <span className="label">
              {t('loupe.sentRefused', { reason: attempt.reason })} · {date.short(attempt.at)}
            </span>
            <Asked ask={attempt.ask} t={t} />
            {attempt.text && <p className="sent__answer">{attempt.text}</p>}
          </div>
        ))}
        {sent?.steps.map((step, index) => (
          <div key={index} className="sent__step">
            <span className="label">{t('loupe.sentMessage', { n: index + 1 })}</span>
            <Asked ask={step.ask} t={t} />
            <span className="label">{t('loupe.sentAnswer')}</span>
            <div className="sent__reply">
              {step.reply.map((part, at) =>
                part.assetId ? (
                  <figure key={at} className={part.thought ? 'is-draft' : undefined}>
                    <img src={assetUrl(part.assetId)} alt="" loading="lazy" />
                    {part.thought && <figcaption>{t('loupe.sentDraft')}</figcaption>}
                  </figure>
                ) : part.text ? (
                  <p key={at} className={`sent__answer ${part.thought ? 'faint' : ''}`}>
                    {part.text}
                  </p>
                ) : null,
              )}
            </div>
          </div>
        ))}
        {sent && sent.calls.length > 0 && (
          <div className="sent__calls">
            <span className="label">{t('loupe.sentCalls')}</span>
            {sent.calls.map((call, index) => (
              <span key={index} className="mono faint">
                {call.stage} · {call.model} · {usd(call.costMicros, 3)} · {(call.ms / 1000).toFixed(0)} s
              </span>
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

/** One message as it went: each picture with its number and caption, then the words. */
function Asked({ ask, t }: { ask: SentStep['ask']; t: Translate }) {
  return (
    <>
      {ask.images.length > 0 && (
        <ol className="sent__images">
          {ask.images.map((image, at) => (
            <li key={`${image.assetId}-${at}`}>
              <img src={image.exact ? assetUrl(image.assetId) : sentUrl(image)} alt="" loading="lazy" />
              <span className="sent__caption">
                <b>Image {at + 1}</b> — {image.label}
                {image.grey && <em> · {t('loupe.sentGrey')}</em>}
                {image.veil?.length ? <em> · {t('loupe.sentVeiled')}</em> : null}
                {image.unveiled && <em> · {t(`loupe.sentUnveiled.${image.unveiled}`)}</em>}
              </span>
            </li>
          ))}
        </ol>
      )}
      <pre className="sent__text">{ask.text}</pre>
    </>
  );
}

/**
 * For the studio's staff: a frame of their own offered to every client as a
 * scene of the Studio — a client's model and pieces then take the place of
 * the ones in it.
 */
export function PublishScene({ shot, t }: { shot: Shot; t: Translate }) {
  const { user, authDisabled } = useSession();
  const { prompt } = useDialog();
  const toast = useToast();
  const describe = useErrorText();
  const [busy, setBusy] = useState(false);

  if ((!authDisabled && !user?.staff) || shot.status !== 'done' || !shot.assetId) return null;

  const publish = async () => {
    const name = await prompt({
      title: t('loupe.publishScene'),
      label: t('loupe.sceneName'),
      value: '',
      confirmLabel: t('common.save'),
    });
    if (name === undefined) return;
    setBusy(true);
    try {
      const scene = await api.sceneFromShot(shot.id, name.trim());
      toast(t('loupe.sceneOffered', { name: scene.name }), 'ok');
    } catch (err) {
      toast(describe(err, 'cat.failed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" className="btn btn--block" disabled={busy} onClick={() => void publish()}>
      {busy ? <span className="spinner" /> : null}
      {t('loupe.publishScene')}
    </button>
  );
}
